import React from 'react';
import { useInView } from 'react-intersection-observer';
import CertificateCarousel from './CertificateCarousel';
import './Certifications.css';

const Certifications = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const organizations = [
    {
      name: '(ISC)²',
      fullName: 'International Information System Security Certification Consortium',
      description: 'A global non-profit organization that provides information security education and certifications, most notably the CISSP (Certified Information Systems Security Professional).',
      focus: 'Information Security, Cybersecurity Certifications',
      headquarters: 'Virginia, USA',
      website: 'https://www.isc2.org/',
      logo: '/Certificates/CC_ISC2.png',
      certifications: [
        {
          name: 'Certified in Cybersecurity (CC)',
          description: 'An entry-level certification that establishes a foundational understanding of cybersecurity principles and best practices for individuals looking to start a career in the field.',
          expertise: 'Foundational Cybersecurity, Security Principles, Security Operations',
          logo: '/Certificates/ISC2_CC logo2.png'
        }
      ]
    },
    {
      name: 'OCEG',
      fullName: 'Open Compliance and Ethics Group',
      description: 'A global, non-profit organization that helps organizations achieve principled performance by integrating governance, risk management, and compliance (GRC) processes.',
      focus: 'Governance, Risk Management, and Compliance (GRC)',
      headquarters: 'Arizona, USA',
      website: 'https://www.oceg.org/',
      logo: '/Certificates/OCEG.png',
      certifications: [
        {
          name: 'Governance, Risk, and Compliance Audit (GRCA)',
          description: 'A professional certificate focused on the principles and practices for auditing an organization\'s GRC capability, specifically using the OCEG GRC Capability Model.',
          expertise: 'Auditing GRC Systems, GRC Capability Assessment, Internal Controls',
          logo: '/Certificates/OCEG_GRCA Logo.png'
        },
        {
          name: 'Governance, Risk and Compliance Professional (GRCP)',
          description: 'A professional certificate that demonstrates knowledge of core GRC concepts, the GRC Capability Model, and how to integrate governance, risk, and compliance activities.',
          expertise: 'GRC Strategy, GRC Integration, Compliance Management, Risk Management',
          logo: '/Certificates/GRCP_OCEG.png'
        },
        {
          name: 'Integrated Policy Management Professional (IPMP)',
          description: 'A professional certificate that validates expertise in developing, implementing, and managing an effective, integrated policy management framework across an organization.',
          expertise: 'Policy Management, Policy Lifecycle, Policy Architecture, Policy Communication',
          logo: '/Certificates/OCEG_IPMP logo.png'
        }
      ]
    },
    {
      name: 'ISACA',
      fullName: 'The Information Systems Audit and Control Association',
      description: 'A global organization for IT governance, control, security, risk management, and assurance professionals. It offers certifications like CISA (Certified Information Systems Auditor) and CISM (Certified Information Security Manager).',
      focus: 'IT Audit, Governance, Security, and Risk Management',
      headquarters: 'Illinois, USA',
      website: 'https://www.isaca.org/',
      logo: '/Certificates/CISA_ISACA.png',
      certifications: [
        {
          name: 'Certified Information System Auditor (CISA)',
          description: 'A globally recognized certification for IS audit, control, and security professionals that verifies the ability to audit, control, monitor, and assess an organization\'s information technology and business systems.',
          expertise: 'IT Auditing, Information Systems Control, IT Governance, Risk Management',
          logo: '/Certificates/ISACA_CISA Logo.png'
        }
      ]
    },
    {
      name: 'ACFE',
      fullName: 'Association of Certified Fraud Examiners',
      description: 'A professional organization dedicated to providing anti-fraud training and education. It administers the CFE (Certified Fraud Examiner) credential.',
      focus: 'Fraud Examination and Prevention',
      headquarters: 'Texas, USA',
      website: 'https://www.acfe.com/',
      logo: '/Certificates/CFE_ACFE.png',
      certifications: [
        {
          name: 'Certified Fraud Examiner (CFE)',
          description: 'A globally recognized credential for individuals who specialize in fraud prevention, detection, and deterrence. It covers financial transactions, law, investigation, and criminology.',
          expertise: 'Fraud Examination, Financial Forensics, Legal Elements of Fraud, Fraud Risk Management',
          logo: '/Certificates/ACFE Logo3.png'
        }
      ]
    },
    {
      name: 'IIA',
      fullName: 'The Institute of Internal Auditors',
      description: 'A global professional association that serves as the internal audit profession\'s voice, recognized authority, and principal educator. It offers the CIA (Certified Internal Auditor) designation.',
      focus: 'Internal Auditing, Governance, Risk, and Control',
      headquarters: 'Florida, USA',
      website: 'https://www.theiia.org/',
      logo: '/Certificates/CIA.png',
      certifications: [
        {
          name: 'Certified Internal Auditor (CIA)',
          description: 'The only globally accepted certification for internal audit professionals. It demonstrates competence in the principles and practices of internal auditing.',
          expertise: 'Internal Audit Practice, Internal Controls, Risk Management, Governance',
          logo: '/Certificates/IIA_CIA Logo1.png'
        }
      ]
    },
    {
      name: 'PICPA',
      fullName: 'Philippine Institute of Certified Public Accountants',
      description: 'The accredited professional organization for CPAs in the Philippines, responsible for the advancement of the accountancy profession.',
      focus: 'Accountancy, Financial Reporting, Auditing (Philippines)',
      headquarters: 'Manila, Philippines',
      website: 'https://picpa.com.ph/',
      logo: '/Certificates/PICPA Logo.png',
      certifications: [
        {
          name: 'Certified Public Accountant (CPA)',
          description: 'A licensed qualification for professionals in public accountancy. This involves knowledge of generally accepted accounting principles (GAAP), auditing, tax, and business law.',
          expertise: 'Accounting, Auditing, Taxation, Financial Reporting, Business Law',
          logo: '/Certificates/PICPA Logo.png'
        }
      ]
    },
    {
      name: 'IOSH',
      fullName: 'Institution of Occupational Safety and Health',
      description: 'A global chartered professional body for safety and health professionals. It works to improve standards and provide training in occupational safety and health.',
      focus: 'Occupational Safety and Health (OSH)',
      headquarters: 'Wigston, UK',
      website: 'https://iosh.com/',
      logo: '/Certificates/IOSH Logo.webp',
      certifications: [
        {
          name: 'IOSH Managing Safely',
          description: 'A globally respected certification that provides managers and supervisors with the knowledge and tools to manage health and safety risks in their teams and organizations effectively.',
          expertise: 'Occupational Safety and Health Management, Risk Assessment, Accident Investigation, Safety Performance Measurement',
          logo: '/Certificates/IOSH_Managing Safely Logo.jpg'
        }
      ]
    }
  ];

  const education = [
    {
      institution: 'Philippine Christian University',
      degree: 'Master\'s in Business Administration (MBA)',
      description: 'A postgraduate degree that provides a broad foundation in business management, leadership, and strategic decision-making. Programs typically cover finance, marketing, operations, and organizational behavior.',
      expertise: 'General Business Management, Leadership, Strategy, and Functional Business Disciplines (e.g., Finance, Marketing, etc.)',
      icon: 'fas fa-graduation-cap'
    },
    {
      institution: 'University of the Cordilleras',
      degree: 'Bachelor of Science in Accountancy (BSA)',
      description: 'An undergraduate degree focused on the principles of financial and management accounting, auditing, and taxation. It is the standard educational path for individuals seeking to become a Certified Public Accountant (CPA).',
      expertise: 'Accounting Principles (GAAP/IFRS), Auditing, Taxation, Financial Reporting, and Cost Accounting',
      icon: 'fas fa-university'
    }
  ];

  return (
    <section id="certifications" className="certifications-section" ref={ref}>
      <div className="container">
        <div className={`certifications-header ${inView ? 'fade-in' : ''}`}>
          <h2 className="section-title center">PROFESSIONAL ORGANIZATIONS & CERTIFICATIONS</h2>
          <div className="title-underline center"></div>
          <p className="certifications-intro">
            Professional memberships and certifications from globally recognized organizations, 
            demonstrating commitment to continuous learning and adherence to the highest industry standards.
          </p>
        </div>

        <div className="organizations-section">

          {organizations.map((org, orgIndex) => (
            <div 
              key={orgIndex} 
              className={`organization-card ${inView ? 'fade-in' : ''}`}
              style={{ animationDelay: `${orgIndex * 0.1}s` }}
            >
              <div className="org-header">
                <div className="org-badge">
                  {org.logo ? (
                    <img src={org.logo} alt={`${org.name} Logo`} className="org-logo-img" />
                  ) : (
                    <span className="org-acronym">{org.name}</span>
                  )}
                </div>
                <div className="org-info">
                  <h4 className="org-name">{org.fullName}</h4>
                  <div className="org-meta">
                    <span className="org-location">
                      <i className="fas fa-map-marker-alt"></i>
                      {org.headquarters}
                    </span>
                    <a href={org.website} target="_blank" rel="noopener noreferrer" className="org-website">
                      <i className="fas fa-globe"></i>
                      Visit Website
                    </a>
                  </div>
                </div>
              </div>

              <div className="org-content">
                <p className="org-description">{org.description}</p>
                <div className="org-focus">
                  <i className="fas fa-bullseye"></i>
                  <strong>Focus Area:</strong> {org.focus}
                </div>
              </div>

              <div className="certifications-list">
                {org.certifications.map((cert, certIndex) => (
                  <div key={certIndex} className="certification-item">
                    <div className="cert-badge">
                      {cert.logo ? (
                        <img src={cert.logo} alt={`${cert.name} Logo`} className="cert-logo-img" />
                      ) : (
                        <i className="fas fa-award"></i>
                      )}
                    </div>
                    <div className="cert-details">
                      <h5 className="cert-name">{cert.name}</h5>
                      <p className="cert-description">{cert.description}</p>
                      <div className="cert-expertise">
                        <i className="fas fa-check-circle"></i>
                        <span><strong>Expertise:</strong> {cert.expertise}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className={`education-section ${inView ? 'fade-in' : ''}`}>
          <h3 className="subsection-title">
            <i className="fas fa-user-graduate"></i>
            Educational Background
          </h3>

          <div className="education-grid">
            {education.map((edu, eduIndex) => (
              <div 
                key={eduIndex} 
                className={`education-card ${inView ? 'fade-in' : ''}`}
                style={{ animationDelay: `${(organizations.length + eduIndex) * 0.1}s` }}
              >
                <div className="edu-icon">
                  <i className={edu.icon}></i>
                </div>
                <div className="edu-content">
                  <h4 className="edu-institution">{edu.institution}</h4>
                  <div className="edu-degree">{edu.degree}</div>
                  <p className="edu-description">{edu.description}</p>
                  <div className="edu-expertise">
                    <i className="fas fa-lightbulb"></i>
                    <span><strong>Expertise Area:</strong> {edu.expertise}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`certifications-summary ${inView ? 'fade-in' : ''}`}>
          <div className="summary-box">
            <i className="fas fa-medal"></i>
            <div className="summary-text">
              <h4>8 Professional Certifications</h4>
              <p>
                From 7 globally recognized organizations spanning Finance, Audit, Risk, Compliance, 
                Cybersecurity, and Business Excellence
              </p>
            </div>
          </div>
          <div className="summary-box">
            <i className="fas fa-globe-americas"></i>
            <div className="summary-text">
              <h4>International Recognition</h4>
              <p>
                Credentials recognized across USA, UK, Philippines, and globally accepted by 
                Fortune 500 companies and regulatory bodies
              </p>
            </div>
          </div>
        </div>

        <CertificateCarousel />
      </div>
    </section>
  );
};

export default Certifications;

